<template>
  <!--  MULTI NODE POOLS  -->
  <runai-dual-list-draggable
    v-if="!isOnlyDefaultNodePool"
    :sortable-list="sortableList"
    :non-sortable-list="nonSortableList"
    row-key="nodePool"
    class="node-pools-list-wrapper"
    separator-text="Drag items between lists"
    @sortable-list-change="onSortableListChange"
  >
    <template #sortable-list>
      <li class="item-wrapper" v-for="(item, index) in sortableListResources" :key="getNodePoolNameByResource(item)">
        <div class="item-index" aid="node-pool-index">{{ index + 1 }}</div>
        <div class="item-content draggable">
          <q-icon
            name="fas fa-grip-dots-vertical"
            class="draggable-icon q-mr-md"
            size="24px"
            style="color: rgba(0, 0, 0, 0.12)"
          />
          <node-pool-row
            :options="nodePoolOptions"
            :model="getNodePoolModel(item)"
            @update-node-pool-over-quota="updateNodePoolResourceOverQuota"
            @update-node-pool-resource="updateNodePoolResource"
            @update-priority="updatePriority"
          />
        </div>
      </li>
    </template>
    <template #non-sortable-list>
      <li class="item-wrapper" v-for="item in nonSortableListResources" :key="getNodePoolNameByResource(item)">
        <div class="item-index" aid="node-pool-index" />
        <div class="item-content draggable">
          <q-icon
            name="fas fa-grip-dots-vertical"
            class="draggable-icon q-mr-md"
            size="24px"
            style="color: rgba(0, 0, 0, 0.12)"
          />
          <node-pool-row
            :options="nodePoolOptions"
            :model="getNodePoolModel(item)"
            @update-node-pool-over-quota="updateNodePoolResourceOverQuota"
            @update-node-pool-resource="updateNodePoolResource"
            @update-priority="updatePriority"
          />
        </div>
      </li>
    </template>
    <template #separator>
      <div class="separator">Node pools above this line will be used when submitting a workload</div>
    </template>
  </runai-dual-list-draggable>

  <!--  SINGLE NODE POOL  -->
  <div v-else class="node-pools-list-wrapper">
    <div class="item-wrapper" v-for="item in resources" :key="getNodePoolNameByResource(item)">
      <div class="item-icon"><runai-svg-icon :name="iconName" size="22" /></div>
      <div class="item-content">
        <node-pool-row
          :options="nodePoolOptions"
          :model="getNodePoolModel(item)"
          @update-node-pool-over-quota="updateNodePoolResourceOverQuota"
          @update-node-pool-resource="updateNodePoolResource"
          @update-priority="updatePriority"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, inject, type PropType } from "vue";
import { NodePoolRow } from "@/components/org-unit/quota-management/node-pools-table/node-pool-row/";
import { RunaiDualListDraggable } from "@/components/common/runai-draggable-list-order/runai-dual-list-draggable";
import {
  CPU_VALUE_FACTOR,
  defaultQuotaOption,
  defaultQuotaPriorityOption,
  type DepartmentNodePoolResourcesSumRecord,
  EQuotaEntity,
  EResourceType,
  type IClusterTotalResources,
  type IDepartmentNodePoolResourcesSum,
  type INodePoolAllocatedNonPreemptibleSum,
  type INodePoolIsOverQuota,
  type INodePoolIsOverQuotaRecord,
  type INodePoolResourcesSum,
  type NodePoolAllocatedNonPreemptibleSumRecord,
  type NodePoolResourcesSumRecord,
} from "@/models/resource.model";
import type { ISelectOption } from "@/models/global.model";
import { useSettingStore } from "@/stores/setting.store";
import { resourceUtil } from "@/utils/resource.util";
import { deepCopy } from "@/utils/common.util";
import { NodeTelemetryType, type TelemetryResponseValuesInner } from "@/swagger-models/cluster-service-client";
import { clusterService } from "@/services/control-plane/cluster.service/cluster.service";
import type {
  AggregatedResources,
  Project,
  ProjectCreationRequest,
  QuotaStatusNodePool,
  Resources,
  ResourcesCpu,
  ResourcesMemory,
} from "@/swagger-models/org-unit-service-client";
import { orgUnitUtil } from "@/utils/org-unit.util";
import type { EQuotaTab, NodePoolModel, NodePoolOptions } from "@/models/org-unit.model";
import { RunaiSvgIcon } from "@/components/common/runai-svg-icon";
import { useClusterStore } from "@/stores/cluster.store";
import { MIN_CLUSTER_VERSION_FOR_ORG_UNIT_RESOURCE_PRIORITY } from "@/common/version.constant";

export enum ENodePoolsTableBodyEmitName {
  UpdateResources = "update:resources",
  UpdateNodePoolsPriorities = "update:node-pools-priorities",
}

const defaultIsNodePoolOverQuota: INodePoolIsOverQuota = { gpu: false, cpu: false, memory: false };
const defaultProjectsDeservedResources: INodePoolResourcesSum = { gpu: 0, cpu: 0, memory: 0 };
const defaultDepartmentDeservedResources: IDepartmentNodePoolResourcesSum = { gpu: 0, cpu: null, memory: null };
const defaultAllocatedNonPreemptibleResources: INodePoolAllocatedNonPreemptibleSum = {
  gpu: 0,
  cpu: 0,
  memory: 0,
};

export default defineComponent({
  components: {
    RunaiSvgIcon,
    RunaiDualListDraggable,
    NodePoolRow,
  },
  emits: {
    [ENodePoolsTableBodyEmitName.UpdateResources]: (resources: Resources[]) => resources,
    [ENodePoolsTableBodyEmitName.UpdateNodePoolsPriorities]: (priorities: string[]) => priorities,
  },
  inject: ["entity"],
  props: {
    project: {
      type: Object as PropType<Project | ProjectCreationRequest>,
      required: false,
    },
    departmentId: {
      type: String as PropType<string>,
      required: false,
    },
    clusterId: {
      type: String as PropType<string>,
      default: "",
    },
    resources: {
      type: Array as PropType<Array<Resources>>,
      required: true,
    },
    nodePoolsPriorities: {
      type: Array as PropType<Array<string>>,
      default: () => [],
    },
    readOnly: {
      type: Boolean as PropType<boolean>,
      required: true,
    },
    isCpuEnabled: {
      type: Boolean as PropType<boolean>,
      required: true,
    },
    isOverQuotaPriorityEnabled: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    quotaStatuses: {
      type: Array as PropType<Array<QuotaStatusNodePool>>,
      default: () => [],
    },
    selectedTab: {
      type: String as PropType<EQuotaTab>,
      required: true,
    },
  },
  data() {
    return {
      settingStore: useSettingStore(),
      clusterStore: useClusterStore(),
      selectedOverQuotaOption: defaultQuotaOption as ISelectOption,
      selectedOverQuotaPriorityOption: defaultQuotaPriorityOption as ISelectOption,
      selectedOverQuotaPriorityOptions: [] as Array<ISelectOption>,
      selectedOverQuotaOptions: [] as Array<ISelectOption>,
      priorities: [...this.nodePoolsPriorities] as Array<string>,
      totalClusterGpu: 0 as number,
      totalClusterCpuCores: 0 as number,
      totalClusterCpuMemoryBytes: 0 as number,
      initialProjectResources: [] as Resources[],
      projectsAggregatedResources: inject<AggregatedResources[]>("projectsAggregatedResources") || [],
      departmentResources: inject<Resources[]>("departmentResources") || [],
    };
  },
  async created() {
    if (this.isDepartmentEntity) {
      await this.loadClusterTotalResources();
    }

    if (this.isOverQuotaPriorityEnabled) {
      this.initOverQuotaPriorityOptions();
    } else {
      this.initOverQuotaOptions();
    }
    this.initialProjectResources = deepCopy(this.resources);
  },
  computed: {
    isDepartmentEntity(): boolean {
      return this.entity === EQuotaEntity.department;
    },
    sortedDepartmentNodePools(): Resources[] {
      return resourceUtil.sortNodePools(this.departmentResources);
    },
    departmentDeservedResources(): DepartmentNodePoolResourcesSumRecord | null {
      if (!this.departmentResources) return null;

      const nodePoolAggregatedResources: DepartmentNodePoolResourcesSumRecord = {};
      this.departmentResources.forEach((resources) => {
        nodePoolAggregatedResources[this.getNodePoolNameByResource(resources)] = {
          gpu: resources.gpu.deserved,
          cpu: resources.cpu?.deserved,
          memory: resources.memory?.deserved,
        };
      });
      return nodePoolAggregatedResources;
    },
    projectsDeservedResources(): NodePoolResourcesSumRecord | null {
      if (this.projectsAggregatedResources.length === 0) return null;

      const nodePoolAggregatedResources: NodePoolResourcesSumRecord = {};
      this.projectsAggregatedResources?.forEach((projectsAggregatedResources) => {
        nodePoolAggregatedResources[projectsAggregatedResources.nodePool.name] = {
          gpu: projectsAggregatedResources.gpu?.deserved || 0,
          cpu: projectsAggregatedResources.cpu?.deserved || 0,
          memory: projectsAggregatedResources.memory?.deserved || 0,
        };
      });

      if (this.entity === EQuotaEntity.project) {
        this.resources.forEach((resources) => {
          const nodePoolName = this.getNodePoolNameByResource(resources);
          nodePoolAggregatedResources[nodePoolName].gpu += resources.gpu.deserved;
          nodePoolAggregatedResources[nodePoolName].cpu += resources.cpu?.deserved || 0;
          nodePoolAggregatedResources[nodePoolName].memory += resources?.memory?.deserved || 0;
        });

        this.initialProjectResources.forEach((resources) => {
          const nodePoolName = this.getNodePoolNameByResource(resources);
          nodePoolAggregatedResources[nodePoolName].gpu -= resources.gpu.deserved;
          nodePoolAggregatedResources[nodePoolName].cpu -= resources.cpu?.deserved || 0;
          nodePoolAggregatedResources[nodePoolName].memory -= resources?.memory?.deserved || 0;
        });
      }

      return nodePoolAggregatedResources;
    },
    isOnlyDefaultNodePool(): boolean {
      return this.resources.length === 1;
    },
    allocatedNonPreemptibleResources(): NodePoolAllocatedNonPreemptibleSumRecord {
      return resourceUtil.allocatedNonPreemptibleResources(this.quotaStatuses);
    },
    isNodePoolOverQuota(): INodePoolIsOverQuotaRecord {
      return this.sortedDepartmentNodePools.reduce(
        (nodePoolOverQuotaMap: INodePoolIsOverQuotaRecord, resources: Resources) => {
          const nodePoolName = this.getNodePoolNameByResource(resources);
          if (!this.projectsDeservedResources || !this.departmentDeservedResources) return nodePoolOverQuotaMap;
          nodePoolOverQuotaMap[nodePoolName] = resourceUtil.computeNodePoolResourceExceedsQuota(
            this.projectsDeservedResources?.[nodePoolName],
            this.departmentDeservedResources?.[nodePoolName] as INodePoolResourcesSum,
          );
          return nodePoolOverQuotaMap;
        },
        {} as INodePoolIsOverQuotaRecord,
      );
    },
    iconName(): string {
      return this.entity === EQuotaEntity.project ? "project-icon" : "departments-icon";
    },
    totalClusterResources(): IClusterTotalResources {
      return {
        gpu: this.totalClusterGpu,
        cpuCores: this.totalClusterCpuCores,
        memoryBytes: this.totalClusterCpuMemoryBytes,
      };
    },
    sortableList(): Array<string> {
      return this.resources
        .filter((resource) => this.nodePoolsPriorities.includes(this.getNodePoolNameByResource(resource)))
        .sort((a, b) => {
          return (
            this.nodePoolsPriorities.indexOf(this.getNodePoolNameByResource(a)) -
            this.nodePoolsPriorities.indexOf(this.getNodePoolNameByResource(b))
          );
        })
        .map((resource) => this.getNodePoolNameByResource(resource));
    },
    sortableListResources(): Array<Resources> {
      return this.resources
        .filter((resource) => this.nodePoolsPriorities.includes(this.getNodePoolNameByResource(resource)))
        .sort((a, b) => {
          return (
            this.nodePoolsPriorities.indexOf(this.getNodePoolNameByResource(a)) -
            this.nodePoolsPriorities.indexOf(this.getNodePoolNameByResource(b))
          );
        });
    },
    nonSortableList(): Array<string> {
      return this.resources
        .filter((resources) => !this.nodePoolsPriorities.includes(this.getNodePoolNameByResource(resources)))
        .map((resource) => this.getNodePoolNameByResource(resource));
    },
    nonSortableListResources(): Array<Resources> {
      return this.resources.filter(
        (resources) => !this.nodePoolsPriorities.includes(this.getNodePoolNameByResource(resources)),
      );
    },
    isClusterSupportsPriority(): boolean {
      return this.clusterStore.isClusterVersionSufficient(
        this.clusterId,
        MIN_CLUSTER_VERSION_FOR_ORG_UNIT_RESOURCE_PRIORITY,
      );
    },
    nodePoolOptions(): NodePoolOptions {
      return {
        readOnly: this.readOnly,
        isCpuEnabled: this.isCpuEnabled,
        isOnlyDefaultNodePool: this.isOnlyDefaultNodePool,
        isOverQuotaPriorityEnabled: this.isOverQuotaPriorityEnabled,
        selectedTab: this.selectedTab,
        isClusterSupportsPriority: this.isClusterSupportsPriority,
      };
    },
  },
  methods: {
    getNodePoolModel(resource: Resources): NodePoolModel {
      const nodePoolName = this.getNodePoolNameByResource(resource);

      return {
        resource,
        priorities: this.priorities,
        isNodePoolOverQuota: this.isNodePoolOverQuota?.[nodePoolName] || defaultIsNodePoolOverQuota,
        projectsDeservedResources: this.projectsDeservedResources?.[nodePoolName] || defaultProjectsDeservedResources,
        departmentDeservedResources:
          this.departmentDeservedResources?.[nodePoolName] || defaultDepartmentDeservedResources,
        allocatedNonPreemptibleResources:
          this.allocatedNonPreemptibleResources?.[nodePoolName] || defaultAllocatedNonPreemptibleResources,
        totalClusterResources: this.totalClusterResources,
      };
    },
    onSortableListChange(priorities: Array<string>): void {
      this.$emit(ENodePoolsTableBodyEmitName.UpdateNodePoolsPriorities, priorities);
    },
    updateNodePoolResource({
      name,
      resourceType,
      resourceValue,
    }: {
      name: string;
      resourceType: EResourceType;
      resourceValue: number | null;
    }): void {
      const resources = deepCopy(this.resources);
      const nodePoolIndex = this.findNodePoolIndexByName(name);

      switch (resourceType) {
        case EResourceType.GPU:
          (resources[nodePoolIndex].gpu as ResourcesCpu).deserved = resourceValue;
          if (!orgUnitUtil.isResourceOverQuotaEnabled(resources[nodePoolIndex], this.isOverQuotaPriorityEnabled)) {
            (resources[nodePoolIndex].gpu as ResourcesCpu).limit = resourceValue;
          }
          break;
        case EResourceType.GPU_LIMIT:
          (resources[nodePoolIndex].gpu as ResourcesCpu).limit = resourceValue;
          break;
        case EResourceType.CPU:
          (resources[nodePoolIndex][resourceType] as ResourcesCpu).deserved =
            resourceValue === null ? resourceValue : resourceValue * CPU_VALUE_FACTOR;
          break;
        default:
          (resources[nodePoolIndex][resourceType] as ResourcesMemory).deserved = resourceValue;
          break;
      }

      this.$emit(ENodePoolsTableBodyEmitName.UpdateResources, resources);
    },
    updateNodePoolResourceOverQuota({ name, overQuota }: { name: string; overQuota: number }): void {
      const resources = deepCopy(this.resources);
      const nodePoolIndex = this.findNodePoolIndexByName(name);
      if (nodePoolIndex === -1) return;

      resources[nodePoolIndex].gpu.overQuotaWeight = overQuota;
      if (resources?.[nodePoolIndex]?.cpu && resources?.[nodePoolIndex]?.memory) {
        (resources[nodePoolIndex].cpu as ResourcesCpu).overQuotaWeight = overQuota;
        (resources[nodePoolIndex].memory as ResourcesMemory).overQuotaWeight = overQuota;
      }
      this.$emit(ENodePoolsTableBodyEmitName.UpdateResources, resources);
    },
    updatePriority({ priority, name }: { priority: string; name: string }): void {
      const resources = deepCopy(this.resources);
      const nodePoolIndex = this.findNodePoolIndexByName(name);
      if (nodePoolIndex === -1) return;
      resources[nodePoolIndex].priority = priority;
      this.$emit(ENodePoolsTableBodyEmitName.UpdateResources, resources);
    },
    findNodePoolIndexByName(name: string): number {
      return this.resources.findIndex((resources: Resources) => this.getNodePoolNameByResource(resources) === name);
    },
    initOverQuotaPriorityOptions(): void {
      this.selectedOverQuotaPriorityOptions = this.resources.map((nodePool) =>
        this.getSelectedOverQuotaOption(nodePool.gpu.overQuotaWeight),
      );
    },
    getSelectedOverQuotaPriorityOption(overQuotaPriority: number | null | undefined): ISelectOption {
      if (overQuotaPriority === null || overQuotaPriority === undefined) {
        return defaultQuotaPriorityOption;
      }
      return {
        label: resourceUtil.getOverQuotaPriorityKeyByValue(overQuotaPriority),
        value: overQuotaPriority,
      };
    },
    initOverQuotaOptions(): void {
      this.selectedOverQuotaOptions = this.resources.map((nodePool) =>
        this.getSelectedOverQuotaPriorityOption(nodePool.gpu.overQuotaWeight),
      );
    },
    getSelectedOverQuotaOption(overQuota: number | null | undefined): ISelectOption {
      if (overQuota === null || overQuota === undefined) {
        return defaultQuotaOption;
      }
      return {
        label: resourceUtil.getOverQuotaKeyByValue(overQuota),
        value: overQuota,
      };
    },
    async loadClusterTotalResources(): Promise<void> {
      const response = await Promise.all([
        this.getNodeTelemetry(NodeTelemetryType.TotalGpus),
        this.getNodeTelemetry(NodeTelemetryType.TotalCpuCores),
        this.getNodeTelemetry(NodeTelemetryType.TotalCpuMemoryBytes),
      ]);
      this.totalClusterGpu = this.sumTelemetryValues(response[0]);
      this.totalClusterCpuCores = this.sumTelemetryValues(response[1]);
      this.totalClusterCpuMemoryBytes = this.sumTelemetryValues(response[2]);
    },
    async getNodeTelemetry(type: NodeTelemetryType): Promise<Array<TelemetryResponseValuesInner>> {
      try {
        return (await clusterService.getNodeTelemetry(type, this.clusterId)).values;
      } catch (e) {
        console.error(`Failed to get node telemetry ${type}`, e);
        return [];
      }
    },
    sumTelemetryValues(values: Array<TelemetryResponseValuesInner>): number {
      return values.reduce((acc, curr) => acc + parseFloat(curr.value), 0);
    },
    getNodePoolNameByResource(resources: Resources): string {
      return orgUnitUtil.getNodePoolNameByResource(resources);
    },
  },
});
</script>

<style scoped lang="scss">
.node-pools-list-wrapper {
  .item-wrapper {
    display: flex;
    height: 50px;
    margin-bottom: 10px;

    .item-index,
    .item-icon {
      background-color: $body-background-color;
      border-radius: 4px;
      margin-right: 10px;
      height: 100%;
      width: 55px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .item-content {
      background-color: $body-background-color;
      border-radius: 4px;
      display: flex;
      align-items: center;
      height: 100%;
      width: 100%;
      padding: 0 20px;
      &.draggable {
        cursor: move;
        padding: 0 16px;
      }
    }
  }
  .separator {
    background-color: $black-54;
    border-radius: 4px;
    color: $white;
    font-size: 12px;
    width: 100%;
    text-align: center;
    font-weight: 400;
    height: 25px;
    padding-top: 3px;
    margin-bottom: 10px;
  }
}
</style>
